import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, UsersIcon } from 'lucide-react';
import { degreeCategories, degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { usePageMeta } from '../hooks/usePageMeta';
import { readMatchParams, resolveMatchPrefill, buildMatchHeadline, readCopyOverrides } from '../utils/matchParams';
export function DegreeListingPage() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const navigate = useNavigate();
  const categoryData = degreeCategories.find(c => c.id === category);
  // Anchor and campaign links carry the visitor's context
  // (?degreeInterest=&areaStudy=&mProgram=), which turns this page into a
  // program-specific landing page: it drives the widget, the headline, and the
  // school listings so all three agree. Resolved once — the URL doesn't change
  // while the page is open.
  const [prefill] = useState(() => resolveMatchPrefill(readMatchParams()));
  const [copy] = useState(() => readCopyOverrides());
  const headline = buildMatchHeadline(prefill, categoryData?.name || 'Degree', copy.headline);
  const subhead = copy.subhead || (prefill.programLabel ? `Compare accredited online ${prefill.programLabel.toLowerCase()} programs and find the right fit for your career goals.` : `${categoryData?.description || ''} Compare top accredited programs and find the right fit for your career goals.`);
  usePageMeta(`${headline} | findonlinecollege.com`, `Compare accredited online ${(prefill.programLabel || categoryData?.name || 'degree').toLowerCase()} programs. Get matched free by career goals, schedule, and budget.`, {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'website',
    // Prefill and campaign query strings vary the headline but not the page, so
    // every variant consolidates onto the plain category URL.
    canonical: `/degrees/${category}`
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    degreeInterest: prefill.degreeInterest,
    areaOfStudy: prefill.areaOfStudy || categoryData?.focusValue || '',
    specialization: prefill.specialization
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const generalSpec = categoryData?.focusValue ? getSpecializationsForFocus(categoryData.focusValue).find(s => s.type.toLowerCase().startsWith('general') || s.type.toLowerCase().startsWith('all')) : undefined;
  // Fetch results from API
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load. URL context wins, so the listings match the
  // program named in the headline rather than the whole category.
  useEffect(() => {
    const area = prefill.areaOfStudy || categoryData?.focusValue;
    if (!area) return;
    fetchResults(prefill.degreeInterest, area, prefill.specialization || generalSpec?.value || '');
  }, [categoryData?.focusValue, fetchResults, generalSpec?.value, prefill]);
  // On mobile the search card sits above the listings, so a new search would
  // otherwise leave the visitor looking at the form they just submitted.
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      pendingScrollRef.current = true;
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  useEffect(() => {
    if (isLoading || !pendingScrollRef.current) return;
    const target = resultsRef.current;
    pendingScrollRef.current = false;
    if (!target || window.innerWidth >= 1024) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);
  if (!categoryData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1288" data-mp="x5bfdv0i4wem">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1289" data-mp="t3gdopensh0b">
          Category Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-1290" data-mp="o4dcjg1suuxm">
          We couldn't find the degree category you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1291" data-mp="eghqj6mu48re">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1292" data-mp="4ijrdo3fsrk0">
      {/* Page Header. The illustrated brand band sits behind the title so the
          category page opens on-brand while the copy stays fully legible. */}
      <div className="relative overflow-hidden border-b border-hairline bg-background-alt py-12 md:py-16" data-id="element-1293" data-mp="x78z45yzzv4q">
        <img src="https://cdn.magicpatterns.com/uploads/k8W2HWVxHiuP3fZeA1oasw/ChatGPT_Image_Sep_4,_2026,_11_35_35_AM_1.png" alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" data-id="element-1294" data-mp="1262ansuq5c29" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1295" data-mp="se61nmr3o1j9">
          {/* Breadcrumb structured data mirrors the visible trail so search
              results can show the category path. */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [{
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${window.location.origin}/`
            }, {
              '@type': 'ListItem',
              position: 2,
              name: categoryData.name,
              item: `${window.location.origin}/degrees/${category}`
            }]
          })
        }} data-id="element-1296" data-mp="35n8nmvo61eh9" />
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-ink-secondary mb-6" aria-label="Breadcrumb" data-id="element-1297" data-mp="2vpkf92xn08gm">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1298" data-mp="2qq7rmfyxyxcz">
              <li className="inline-flex items-center" data-id="element-1299" data-mp="2qsatpyddcgy2">
                <Link to="/" className="inline-flex items-center hover:text-primary transition-colors" data-id="element-1300" data-mp="aximcnuaqwg5">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1301" data-mp="5yxkyl3b66q4" />
                  Home
                </Link>
              </li>
              <li data-id="element-1302" data-mp="q3od8wv00r81">
                <div className="flex items-center" data-id="element-1303" data-mp="q00jbwln62ss">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1304" data-mp="v1pwpiwc6sk1" />
                  <span className="ml-1 md:ml-2" data-id="element-1305" data-mp="z2rvxzq974m0">Degrees</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1306" data-mp="13t00e312o52g">
                <div className="flex items-center" data-id="element-1307" data-mp="13u1iuskf3tu7">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1308" data-mp="18sl7x1ub5lqs" />
                  <span className="ml-1 md:ml-2 text-ink font-medium" data-id="element-1309" data-mp="18s35tokysday">
                    {prefill.programLabel || categoryData.name}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-ink" data-id="element-1310" data-mp="1ms4735555mqy">
            {headline}
          </h1>
          <p className="text-lg text-ink-secondary max-w-3xl" data-id="element-1311" data-mp="3kinkkqugjxpd">{subhead}</p>
        </div>
      </div>

      <TrustSignals data-id="element-1312" data-mp="3oipc476hbaqx" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1313" data-mp="3ok8t5zzylptr">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1314" data-mp="3h0hmd6t216v7">
          {/* Main Content - Listings */}
          <div ref={resultsRef} id="school-matches" className="lg:w-3/4 order-2 lg:order-1 scroll-mt-20" data-id="element-1315" data-mp="3c8zo4qif1kdx">
            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1316" data-mp="37829up0j1ahp">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1317" data-mp="33a4et5k2ru6e" />
                <p className="text-gray-600 font-medium" data-id="element-1318" data-mp="3sd9fz36yrqc">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1319" data-mp="3qwt6uk2st30q">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1320" data-mp="3lyj6zw64pe8s" />
                <div data-id="element-1321" data-mp="2fjqnxid881de">
                  <p className="text-red-800 font-medium" data-id="element-1322" data-mp="2fka712em4vt0">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1323" data-mp="2aiunxfvzwzd9">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1324" data-mp="yhm30t13pb73">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1325" data-mp="tqvx9eorj6bv" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1326" data-mp="18f9yjl6j6mqb" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1327" data-mp="13gypvdrz2i9o" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1328" data-mp="1de3edk9aiptn">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1329" data-mp="3j7bf4yaf6iw3">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1330" data-mp="3f6rlw5syvzf8" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1331" data-mp="351t01o30wpqb">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1332" data-mp="3934tk4p0pv3s">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar - Form */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-1333" data-mp="2ld0odsw6lnao">
            <div className="sticky top-24" data-id="element-1334" data-mp="2qd5apzjrtc8t">
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6" data-id="element-1335" data-mp="2qdy7c7p0mld8">
                <div className="bg-navy px-6 py-4" data-id="element-1336" data-mp="2cep0hymp93lx">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1337" data-mp="2hc7213ye0gjw">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1338" data-mp="amsf7xaqcfdy">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1339" data-mp="d7i1vnwtiy9l">
                  {/* Degree Level */}
                  <div data-id="element-1340" data-mp="93adxsy6pmcf">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1341" data-mp="977y8hwi0jxw">
                      <option value="" data-id="element-1342" data-mp="rxpc5gco9az7">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1343" data-mp="n29zusubbn4w">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-1344" data-mp="1fn0iregpdzl9">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-1345" data-mp="1i7ge9e60g59m">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1346" data-mp="1n46q1pas8ep8">
                      <option value="" data-id="element-1347" data-mp="212dg4j9hpaec">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1348" data-mp="2617wlejzimva">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-1349" data-mp="2fsca09u0hzo0">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization / Program */}
                  <div data-id="element-1350" data-mp="2orpq3lcn5mhp">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1351" data-mp="3mif2pki3ret4">
                      <option value="" data-id="element-1352" data-mp="2ykznqc3dzuoi">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1353" data-mp="37kwi8ucgjnrh">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-1354" data-mp="mvff45w5518e">
                        {errors.specialization}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full h-[52px] px-6 bg-primary hover:bg-primary-hover active:bg-secondary disabled:opacity-50 disabled:pointer-events-none text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary flex items-center justify-center gap-2" data-id="element-1355" data-mp="vawb6nywuhek">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1356" data-mp="1j0gvba6svkpf" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Lead capture. The card above filters the listings; this one
                  submits the visitor to the partner APIs, carrying whatever
                  program context is currently selected. */}
              <div className="mb-6" data-id="element-1357" data-mp="21tkrb27hq9n7">
                <HomeInlineLeadCapture degreeInterest={formData.degreeInterest} areaStudy={formData.areaOfStudy || categoryData.focusValue} mProgram={formData.specialization || generalSpec?.value || ''} title="Get Matched Free" subtitle="Accredited schools, no obligation" data-id="element-1358" data-mp="2bo04dxzu64w2" />
              </div>

              {/* Undecided nudge — for visitors who can't fill the form yet */}
              <Link to="/degrees" className="group flex items-center gap-3 bg-light hover:bg-white border border-gray-200 rounded-lg px-4 py-3 mb-6 transition-colors duration-200" data-id="element-1359" data-mp="1sr7ca52ena3b">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white group-hover:bg-light border border-gray-200 flex items-center justify-center text-navy transition-colors duration-200" data-id="element-1360" data-mp="1izul7fofknyc">
                  <UsersIcon className="w-4 h-4" aria-hidden="true" data-id="element-1361" data-mp="19y3blzqrrwz7" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-1362" data-mp="150led6m47671">
                  <span className="block text-sm font-bold text-navy leading-tight" data-id="element-1363" data-mp="3gnojrgpkg4gw">
                    Not sure what to study?
                  </span>
                  <span className="block text-xs text-gray-600 mt-0.5 leading-tight" data-id="element-1364" data-mp="3lkf26aoufc5h">
                    Explore top programs by career
                  </span>
                </span>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-1365" data-mp="3umeif0s1ac7d" />
              </Link>

              {/* Related Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1366" data-mp="31yir6bl8hchz">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1367" data-mp="36w0zbzrffnox">
                  Programs in {categoryData.name}
                </h4>
                <ul className="space-y-2" data-id="element-1368" data-mp="2e9g8r1irzfmf">
                  {getSpecializationsForFocus(formData.areaOfStudy || categoryData.focusValue).map(spec => <li key={spec.value} data-id="element-1369" data-mp="2j66p7pyhsd8d">
                      <button onClick={() => {
                    updateFormData('specialization', spec.value);
                    fetchResults(formData.degreeInterest, formData.areaOfStudy || categoryData.focusValue, spec.value);
                  }} className={`text-sm flex items-center w-full text-left ${formData.specialization === spec.value ? 'text-crimson font-semibold' : 'text-gray-600 hover:text-crimson'}`} data-id="element-1370" data-mp="1ckfaawtqa183">
                        <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1371" data-mp="1vijav2vjurya" />
                        {spec.type}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProgramContentSection degreeInterest={formData.degreeInterest} areaOfStudy={formData.areaOfStudy || categoryData.focusValue} specialization={formData.specialization || generalSpec?.value || ''} data-id="element-1372" data-mp="20a1annlu6mho" />
    </main>;
}